import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowRight, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '../components/ui/carousel';
import Autoplay from 'embla-carousel-autoplay';
import { divisions, pricingPlans } from '../mockData';

const Services = () => {
  const { divisionId } = useParams();
  const division = divisions.find(d => d.id === divisionId);

  if (!division) {
    return (
      <div className="min-h-screen pt-32 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl font-bold text-foreground mb-4">Service Not Found</h1>
          <Link to="/">
            <Button>Return Home</Button>
          </Link>
        </div>
      </div>
    );
  }

  const pricing = pricingPlans[divisionId] || [];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-24 md:pt-32 pb-12 md:pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img
            src={division.image}
            alt={division.name}
            className="w-full h-full object-cover opacity-10"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-background via-background to-purple-900/30" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-primary/10 via-transparent to-transparent" />
        </div>
        <div className="max-w-7xl mx-auto relative">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-block mb-3 md:mb-4 px-3 md:px-4 py-1.5 md:py-2 bg-primary/20 text-primary border border-primary/30 rounded-full text-xs md:text-sm font-semibold backdrop-blur-sm">
              {division.shortName}
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-4 md:mb-6">
              {division.name}
            </h1>
            <p className="text-lg md:text-xl text-primary mb-6 md:mb-8 leading-relaxed font-semibold px-4">
              {division.tagline}
            </p>
            <p className="text-base md:text-lg text-muted-foreground leading-relaxed px-4">
              {division.description}
            </p>
          </div>
        </div>
      </section>

      {/* Featured Image Section */}
      <section className="py-12 md:py-20 bg-secondary/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
            {/* Image first on mobile */}
            <div className="relative order-1 md:order-1">
              <img
                src={
                  divisionId === 'trading' 
                    ? 'https://images.unsplash.com/photo-1613690399151-65ea69478674'
                    : divisionId === 'consultancy'
                    ? 'https://images.unsplash.com/photo-1517048676732-d65bc937f952'
                    : divisionId === 'it-services'
                    ? 'https://images.unsplash.com/photo-1564457461758-8ff96e439e83'
                    : 'https://images.unsplash.com/photo-1598316560453-0246d4611979'
                }
                alt={division.name}
                className="rounded-2xl shadow-2xl shadow-primary/20 border border-border w-full h-64 md:h-[400px] object-cover"
              />
              <div className="absolute -bottom-4 md:-bottom-6 -right-4 md:-right-6 bg-gradient-to-br from-primary to-purple-600 text-white p-4 md:p-6 rounded-xl shadow-xl shadow-primary/50">
                <div className="text-2xl md:text-3xl font-bold">{division.services.length}+</div>
                <div className="text-xs md:text-sm">Services Offered</div>
              </div>
            </div>
            {/* Content second on mobile */}
            <div className="order-2 md:order-2">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4 md:mb-6">What We Offer</h2>
              <p className="text-base md:text-lg text-muted-foreground mb-4 md:mb-6 leading-relaxed">
                {divisionId === 'trading' && 
                  'As your trusted partner in international trade, we provide end-to-end solutions that simplify global commerce. From customs clearance to supply chain optimization, our experienced team ensures your goods move efficiently across borders while maintaining full compliance with international regulations.'
                }
                {divisionId === 'consultancy' && 
                  'Transform your business with strategic guidance from our seasoned consultants. We bring decades of cross-industry experience to help you navigate complex challenges, optimize operations, and achieve sustainable growth. Our data-driven approach ensures every recommendation delivers measurable impact.'
                }
                {divisionId === 'it-services' && 
                  'Keep your business running smoothly with our comprehensive IT infrastructure solutions. From network design to 24/7 monitoring, we provide the technical foundation your organization needs to thrive. Our proactive approach prevents issues before they impact your operations.'
                }
                {divisionId === 'software' && 
                  'Bring your digital vision to life with custom software built for your unique needs. Our expert developers combine cutting-edge technology with best practices to create scalable, secure applications that drive business value. From concept to deployment, we\'re with you every step of the way.'
                }
              </p>
              
              <div className="bg-card/50 border border-border rounded-xl p-4 md:p-6 mb-4 md:mb-6">
                <h3 className="text-lg md:text-xl font-bold text-foreground mb-3 md:mb-4">
                  {divisionId === 'trading' && 'Why Choose Our Trading Services?'}
                  {divisionId === 'consultancy' && 'Why Choose Our Consulting Services?'}
                  {divisionId === 'it-services' && 'Why Choose Our IT Services?'}
                  {divisionId === 'software' && 'Why Choose Our Software Services?'}
                </h3>
                <div className="space-y-3">
                  {divisionId === 'trading' && (
                    <>
                      <div className="flex items-start space-x-3">
                        <CheckCircle className="text-primary mt-1 flex-shrink-0" size={20} />
                        <div>
                          <div className="font-semibold text-foreground">Global Network</div>
                          <div className="text-sm text-muted-foreground">Partnerships across 50+ countries for seamless trade</div>
                        </div>
                      </div>
                      <div className="flex items-start space-x-3">
                        <CheckCircle className="text-primary mt-1 flex-shrink-0" size={20} />
                        <div>
                          <div className="font-semibold text-foreground">Compliance Expertise</div>
                          <div className="text-sm text-muted-foreground">Navigate complex regulations with confidence</div>
                        </div>
                      </div>
                      <div className="flex items-start space-x-3">
                        <CheckCircle className="text-primary mt-1 flex-shrink-0" size={20} />
                        <div>
                          <div className="font-semibold text-foreground">Cost Optimization</div>
                          <div className="text-sm text-muted-foreground">Reduce shipping costs through strategic routing</div>
                        </div>
                      </div>
                    </>
                  )}
                  {divisionId === 'consultancy' && (
                    <>
                      <div className="flex items-start space-x-3">
                        <CheckCircle className="text-primary mt-1 flex-shrink-0" size={20} />
                        <div>
                          <div className="font-semibold text-foreground">Proven Methodology</div>
                          <div className="text-sm text-muted-foreground">Data-driven strategies that deliver results</div>
                        </div>
                      </div>
                      <div className="flex items-start space-x-3">
                        <CheckCircle className="text-primary mt-1 flex-shrink-0" size={20} />
                        <div>
                          <div className="font-semibold text-foreground">Industry Expertise</div>
                          <div className="text-sm text-muted-foreground">Deep knowledge across multiple sectors</div>
                        </div>
                      </div>
                      <div className="flex items-start space-x-3">
                        <CheckCircle className="text-primary mt-1 flex-shrink-0" size={20} />
                        <div>
                          <div className="font-semibold text-foreground">Long-term Partnership</div>
                          <div className="text-sm text-muted-foreground">Ongoing support beyond initial engagement</div>
                        </div>
                      </div>
                    </>
                  )}
                  {divisionId === 'it-services' && (
                    <>
                      <div className="flex items-start space-x-3">
                        <CheckCircle className="text-primary mt-1 flex-shrink-0" size={20} />
                        <div>
                          <div className="font-semibold text-foreground">24/7 Monitoring</div>
                          <div className="text-sm text-muted-foreground">Round-the-clock system surveillance and support</div>
                        </div>
                      </div>
                      <div className="flex items-start space-x-3">
                        <CheckCircle className="text-primary mt-1 flex-shrink-0" size={20} />
                        <div>
                          <div className="font-semibold text-foreground">Proactive Maintenance</div>
                          <div className="text-sm text-muted-foreground">Prevent issues before they impact your business</div>
                        </div>
                      </div>
                      <div className="flex items-start space-x-3">
                        <CheckCircle className="text-primary mt-1 flex-shrink-0" size={20} />
                        <div>
                          <div className="font-semibold text-foreground">Enterprise Security</div>
                          <div className="text-sm text-muted-foreground">Bank-grade security measures and compliance</div>
                        </div>
                      </div>
                    </>
                  )}
                  {divisionId === 'software' && (
                    <>
                      <div className="flex items-start space-x-3">
                        <CheckCircle className="text-primary mt-1 flex-shrink-0" size={20} />
                        <div>
                          <div className="font-semibold text-foreground">Agile Development</div>
                          <div className="text-sm text-muted-foreground">Rapid iterations with continuous feedback</div>
                        </div>
                      </div>
                      <div className="flex items-start space-x-3">
                        <CheckCircle className="text-primary mt-1 flex-shrink-0" size={20} />
                        <div>
                          <div className="font-semibold text-foreground">Scalable Architecture</div>
                          <div className="text-sm text-muted-foreground">Built to grow with your business needs</div>
                        </div>
                      </div>
                      <div className="flex items-start space-x-3">
                        <CheckCircle className="text-primary mt-1 flex-shrink-0" size={20} />
                        <div>
                          <div className="font-semibold text-foreground">Modern Tech Stack</div>
                          <div className="text-sm text-muted-foreground">Latest frameworks and best practices</div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row items-center gap-3 sm:space-x-4">
                <Link to="/contact" className="w-full sm:w-auto">
                  <Button className="w-full sm:w-auto bg-primary hover:bg-primary/90 text-white shadow-lg shadow-primary/50">
                    Get Started
                  </Button>
                </Link>
                <Link to="/payment" state={{ division: division.name }} className="w-full sm:w-auto">
                  <Button variant="outline" className="w-full sm:w-auto border-2 border-primary text-primary hover:bg-primary/10">
                    View Pricing
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid Section */}
      <section className="py-12 md:py-20 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 md:mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-3 md:mb-4">Complete Service Portfolio</h2>
            <p className="text-lg md:text-xl text-muted-foreground px-4">End-to-end solutions for your business needs</p>
          </div>

          {/* Desktop Grid */}
          <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {division.services.map((service, index) => (
              <Card key={index} className="group hover:shadow-xl hover:shadow-primary/30 transition-all duration-300 border-2 hover:border-primary hover:scale-105 overflow-hidden cursor-pointer">
                <CardContent className="p-0">
                  <div className="relative h-32 overflow-hidden bg-gradient-to-br from-primary/10 to-purple-600/10 group-hover:from-primary/30 group-hover:to-purple-600/30 transition-all duration-300">
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-primary/20 via-transparent to-transparent group-hover:from-primary/40 transition-all duration-300" />
                    <div className="absolute bottom-4 left-4 w-12 h-12 bg-primary/20 group-hover:bg-primary rounded-full flex items-center justify-center transition-all duration-300 group-hover:rotate-12 group-hover:scale-110">
                      <CheckCircle className="text-primary group-hover:text-white transition-all duration-300" size={28} />
                    </div>
                  </div>
                  <div className="p-6 group-hover:bg-primary/5 transition-colors duration-300">
                    <h3 className="text-lg font-bold text-foreground group-hover:text-primary transition-colors duration-300 mb-3">
                      {typeof service === 'string' ? service : service.name}
                    </h3>
                    {typeof service === 'object' && service.description && (
                      <p className="text-sm text-muted-foreground group-hover:text-foreground transition-colors duration-300 leading-relaxed">
                        {service.description}
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Mobile/Tablet Slider */}
          <div className="md:hidden">
            <Carousel
              opts={{
                align: "start",
                loop: true,
              }}
              plugins={[
                Autoplay({
                  delay: 3000,
                }),
              ]}
              className="w-full"
            >
              <CarouselContent className="-ml-2">
                {division.services.map((service, index) => (
                  <CarouselItem key={index} className="pl-2 basis-full sm:basis-3/4">
                    <Card className="group hover:shadow-xl hover:shadow-primary/30 transition-all duration-300 border-2 hover:border-primary overflow-hidden cursor-pointer h-full">
                      <CardContent className="p-0">
                        <div className="relative h-32 overflow-hidden bg-gradient-to-br from-primary/10 to-purple-600/10 group-hover:from-primary/30 group-hover:to-purple-600/30 transition-all duration-300">
                          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-primary/20 via-transparent to-transparent group-hover:from-primary/40 transition-all duration-300" />
                          <div className="absolute bottom-4 left-4 w-12 h-12 bg-primary/20 group-hover:bg-primary rounded-full flex items-center justify-center transition-all duration-300 group-hover:rotate-12 group-hover:scale-110">
                            <CheckCircle className="text-primary group-hover:text-white transition-all duration-300" size={28} />
                          </div>
                        </div>
                        <div className="p-6 group-hover:bg-primary/5 transition-colors duration-300">
                          <h3 className="text-lg font-bold text-foreground group-hover:text-primary transition-colors duration-300 mb-3">
                            {typeof service === 'string' ? service : service.name}
                          </h3>
                          {typeof service === 'object' && service.description && (
                            <p className="text-sm text-muted-foreground group-hover:text-foreground transition-colors duration-300 leading-relaxed">
                              {service.description}
                            </p>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious className="hidden sm:flex" />
              <CarouselNext className="hidden sm:flex" />
            </Carousel>
          </div>
        </div>
      </section>

      {/* Industries/Technologies with Images */}
      {division.industries && (
        <section className="py-20 bg-secondary/30 relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_right,_var(--tw-gradient-stops))] from-primary/10 via-transparent to-transparent" />
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
              <div>
                <h2 className="text-4xl font-bold text-foreground mb-4">Industries We Serve</h2>
                <p className="text-xl text-muted-foreground mb-6">
                  Our expertise spans across multiple industries, delivering tailored solutions that understand 
                  and address sector-specific challenges.
                </p>
                <img
                  src={
                    divisionId === 'trading'
                      ? 'https://images.unsplash.com/photo-1606185540834-d6e7483ee1a4'
                      : divisionId === 'consultancy'
                      ? 'https://images.unsplash.com/photo-1709715357520-5e1047a2b691'
                      : 'https://images.unsplash.com/photo-1603201667141-5a2d4c673378'
                  }
                  alt="Industries"
                  className="rounded-xl shadow-xl shadow-primary/20 border border-border w-full h-64 object-cover"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                {division.industries.map((industry, index) => (
                  <Card key={index} className="group hover:shadow-lg shadow-primary/50 transition-all duration-300 hover:border-primary/50 border-2">
                    <CardContent className="p-4 text-center">
                      <div className="w-10 h-10 bg-primary/20 rounded-lg flex items-center justify-center mx-auto mb-3">
                        <div className="w-2 h-2 bg-primary rounded-full group-hover:scale-150 transition-transform" />
                      </div>
                      <h3 className="text-sm font-semibold text-foreground group-hover:text-primary transition-colors">{industry}</h3>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {division.technologies && (
        <section className="py-20 bg-background relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_var(--tw-gradient-stops))] from-primary/10 via-transparent to-transparent" />
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1 grid grid-cols-2 gap-4">
                {division.technologies.map((tech, index) => (
                  <Card key={index} className="group hover:shadow-lg shadow-primary/50 transition-all duration-300 hover:border-primary/50 border-2">
                    <CardContent className="p-4 text-center">
                      <div className="w-10 h-10 bg-primary/20 rounded-lg flex items-center justify-center mx-auto mb-3">
                        <div className="w-2 h-2 bg-primary rounded-full group-hover:scale-150 transition-transform" />
                      </div>
                      <h3 className="text-sm font-semibold text-foreground group-hover:text-primary transition-colors">{tech}</h3>
                    </CardContent>
                  </Card>
                ))}
              </div>
              <div className="order-1 lg:order-2">
                <h2 className="text-4xl font-bold text-foreground mb-4">Technology Stack</h2>
                <p className="text-xl text-muted-foreground mb-6">
                  We leverage cutting-edge technologies and industry-leading tools to build robust, 
                  scalable solutions that drive innovation.
                </p>
                <img
                  src={
                    divisionId === 'it-services'
                      ? 'https://images.unsplash.com/photo-1550751827-4bd374c3f58b'
                      : 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c'
                  }
                  alt="Technology"
                  className="rounded-xl shadow-xl shadow-primary/20 border border-border w-full h-64 object-cover"
                />
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Visual Showcase Gallery */}
      <section className="py-20 bg-secondary/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-foreground mb-4">Our Work in Action</h2>
            <p className="text-xl text-muted-foreground">See how we deliver excellence across projects</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {[
              divisionId === 'trading' 
                ? 'https://images.unsplash.com/photo-1571086291540-b137111fa1c7'
                : divisionId === 'consultancy'
                ? 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40'
                : divisionId === 'it-services'
                ? 'https://images.pexels.com/photos/1148820/pexels-photo-1148820.jpeg'
                : 'https://images.unsplash.com/photo-1607799632518-da91dd151b38',
              
              divisionId === 'trading'
                ? 'https://images.unsplash.com/photo-1644079446600-219068676743'
                : divisionId === 'consultancy'
                ? 'https://images.unsplash.com/photo-1552664730-d307ca884978'
                : divisionId === 'it-services'
                ? 'https://images.unsplash.com/photo-1510915228340-29c85a43dcfe'
                : 'https://images.unsplash.com/photo-1551650975-87deedd944c3',
              
              divisionId === 'trading'
                ? 'https://images.unsplash.com/photo-1542296332-2e4473faf563'
                : divisionId === 'consultancy'
                ? 'https://images.unsplash.com/photo-1523301343968-6a6ebf63c672'
                : divisionId === 'it-services'
                ? 'https://images.pexels.com/photos/60504/security-protection-anti-virus-software-60504.jpeg'
                : 'https://images.unsplash.com/photo-1555066931-4365d14bab8c'
            ].map((img, index) => (
              <div key={index} className="group relative overflow-hidden rounded-xl border-2 border-border hover:border-primary/50 transition-all duration-300">
                <img 
                  src={img} 
                  alt={`${division.name} showcase ${index + 1}`}
                  className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-4 left-4 right-4">
                    <div className="text-white font-semibold">Project {index + 1}</div>
                    <div className="text-primary text-sm">View Details</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      {pricing.length > 0 && (
        <section className="py-20 bg-background">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-foreground mb-4">Pricing Plans</h2>
              <p className="text-xl text-muted-foreground">Flexible options to match your requirements</p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {pricing.map((plan, index) => (
                <Card
                  key={index}
                  className={`relative hover:shadow-2xl transition-all duration-300 ${
                    plan.popular ? 'border-2 border-primary scale-105' : 'border-2 hover:border-primary/50'
                  }`}
                >
                  {plan.popular && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <span className="bg-primary text-white px-4 py-1 rounded-full text-sm font-semibold">
                        Most Popular
                      </span>
                    </div>
                  )}
                  <CardContent className="p-8">
                    <h3 className="text-2xl font-bold text-foreground mb-2">{plan.name}</h3>
                    <div className="mb-6">
                      <span className="text-4xl font-bold text-primary">{plan.price}</span>
                      <span className="text-muted-foreground ml-2">/ {plan.period}</span>
                    </div>
                    <ul className="space-y-3 mb-8">
                      {plan.features.map((feature, i) => (
                        <li key={i} className="flex items-start">
                          <CheckCircle className="text-primary mr-2 flex-shrink-0 mt-0.5" size={20} />
                          <span className="text-muted-foreground">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <Link to="/payment" state={{ division: division.name, plan: plan.name }}>
                      <Button
                        className={`w-full ${
                          plan.popular
                            ? 'bg-primary hover:bg-primary/90 text-white'
                            : 'bg-background hover:bg-primary/10 text-primary border-2 border-primary'
                        } transition-all duration-300`}
                      >
                        Get Started
                        <ArrowRight className="ml-2" size={16} />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-primary/20 via-purple-600/10 to-background relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,_var(--tw-gradient-stops))] from-primary/20 via-transparent to-transparent" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <h2 className="text-4xl font-bold text-foreground mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-muted-foreground mb-8">
            Let's discuss how {division.shortName} can help your business grow
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-primary hover:bg-primary/90 text-white shadow-lg shadow-primary/50 hover:shadow-primary/80 transition-all duration-300">
                Contact Us
              </Button>
            </Link>
            <Link to="/payment" state={{ division: division.name }}>
              <Button size="lg" variant="outline" className="border-2 border-primary text-primary hover:bg-primary/10 shadow-md hover:shadow-lg transition-all duration-300">
                Make a Payment
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
